import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Banner: React.FC = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const slides = useMemo(
    () => [
      {
        image: "/banner/banner-1.jpg",
        title: t("home.bannerSlides.apple.title"),
        button: t("home.bannerSlides.apple.button"),
        searchTerm: "Apple store",
      },
      {
        image: "/banner/banner-2.jpg",
        title: t("home.bannerSlides.vitamins.title"),
        button: t("home.bannerSlides.vitamins.button"),
        searchTerm: "vitamins",
      },
      {
        image: "/banner/banner-3.jpg",
        title: t("home.bannerSlides.brands.title"),
        button: t("home.bannerSlides.brands.button"),
        searchTerm:
          'puma|nike|adidas|reebok|under armour "t-shirt" OR tshirt OR shoes',
      },
    ],
    [t]
  );

  const handlePrev = () =>
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  const handleNext = () =>
    setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));

  const handleButtonClick = (term: string) => {
    const query = encodeURIComponent(term);
    navigate(`/search?query=${query}&retailer=amazon&page=1`);
  };

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 10000);

    return () => clearInterval(timerId);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        mt: 0.5,
        mb: 4,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: "100%", lg: 1200 },
          height: { xs: 200, sm: 260, md: 320, lg: 350 },
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            transition: "transform 0.6s ease-in-out",
            transform: `translateX(-${current * 100}%)`,
          }}
        >
          {slides.map((slide, idx) => (
            <Box
              key={idx}
              sx={{
                minWidth: "100%",
                height: "100%",
                backgroundImage: `url(${slide.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          ))}
        </Box>
        {/* Slide Text */}
        <Typography
          variant="h3"
          className="notranslate"
          translate="no"
          sx={{
            color: "#fff",
            fontWeight: 700,
            position: "absolute",
            left: { xs: 20, sm: 30, md: 35, lg: 40 },
            top: { xs: 30, sm: 40, md: 50, lg: 60 },
            zIndex: 2,
            maxWidth: { xs: 300, sm: 400, md: 500, lg: 600 },
            fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem", lg: "2.5rem" },
            whiteSpace: "pre-line",
          }}
        >
          {slides[current].title}
        </Typography>
        <Button
          variant="contained"
          className="notranslate"
          translate="no"
          onClick={() => handleButtonClick(slides[current].searchTerm)}
          sx={{
            position: "absolute",
            left: { xs: 20, sm: 30, md: 35, lg: 40 },
            top: { xs: 120, sm: 150, md: 170, lg: 200 },
            marginTop: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
            background: "#FF6B6B",
            color: "#fff",
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem", lg: "1.2rem" },
            px: { xs: 2, sm: 3, md: 3.5, lg: 4 },
            py: { xs: 1, sm: 1.25, md: 1.4, lg: 1.5 },
            borderRadius: 2,
            boxShadow: 2,
            "&:hover": { background: "#ff4b4b" },
          }}
        >
          {slides[current].button}
        </Button>
        {/* Arrows */}
        <IconButton
          onClick={handlePrev}
          sx={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
            background: "rgba(0,0,0,0.3)",
            "&:hover": { background: "rgba(0,0,0,0.5)" },
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={handleNext}
          sx={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
            background: "rgba(0,0,0,0.3)",
            "&:hover": { background: "rgba(0,0,0,0.5)" },
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
        {/* Dots */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 20,
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
          }}
        >
          {slides.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: 14,
                height: 6,
                borderRadius: 3,
                background: idx === current ? "#FF6B6B" : "#fff",
                opacity: idx === current ? 1 : 0.5,
                transition: "all 0.2s",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Banner;
